/*
 Id to asset mapping can be found here:
 https://github.com/sharethrough/adserver/blob/cbbf6f9e1f8b37af704f1dce877236109d8122b4/real_time_bid/src/main/scala/com/sharethrough/rtb/model/request/RequestAssets.scala#L27
*/

'use strict';

import _ from 'underscore';

export default function AdMarkupService(
  BeaconsService,
  CreativeService,
  CurrentPartnerService,
  RtbPixelService
) {
  var vm = this;
  var VIDEO_PIXEL_URL = 'https://b.sharethrough.com/video_swap?key=';
  const DISPLAY_PIXEL_URL = 'https://b.sharethrough.com/native_display?key=';
  vm.generate = generate;
  vm.markup = null;

  /*
      Video creatives are weird. Instead of just generating markup client side
      (how a native display ad works), we're outputting markup that contains an
      impression pixel (which behaves just like the DSP 2.3 workaround).

      Here are the differences between the VAST and mp4 markup:

      - link.url
        - A VAST video has no link.url. We DON'T add this field at all. An empty
          string value breaks the ad server

        - An mp4 video has a link.url, but it's a workaround to include a clickout
          tracker. This is the not actually the media url (which IS the clickout for
          native display), but it's the custom engagement button URL. This is then
          wrapped in the DSP's clickMacro so they know to track it as a clickout.

      - vasttag
        - A VAST video needs a fake VAST tag thrown in. <FOO></FOO>. This is so
          the adserver knows it's a VAST video, but it reads the real VAST tag from
          the impression pixel.

        - An mp4 obviously doesn't have a vasttag, but I'm including this because
          that wasn't always the case (I know right)
    */

  function isVideoCreative(creative) {
    return _.contains(
      ['hosted-video', 'outstream', 'native-outstream'],
      creative.action
    );
  }

  function isIsiCreative(creative) {
    return creative.isiText && creative.isiText.length > 0;
  }

  function generate(creative) {
    // TODO: remove this once all callsites pass creative param
    creative = creative || CreativeService.creative;
    return CurrentPartnerService.get().then(function(partner) {
      vm.partner = partner;
      if (
        isVideoCreative(creative) ||
        isIsiCreative(creative)
      ) {
        return buildImpTrackerMarkup(creative);
      } else {
        return buildMarkup(creative);
      }
    });
  }

  function buildImpTrackerMarkup(creative) {
    return RtbPixelService.findOrCreate().then(() => buildMarkup(creative));
  }

  function buildMarkup(creative) {
    var markup = { native: {} };

    markup.native.link = buildLink(creative);
    markup.native.imptrackers = buildImpTrackers(creative);
    markup.native.assets = buildAssets(creative);

    if (creative.jsTracker) {
      markup.native.jstracker = creative.jsTracker;
    }
    // TODO: See if we need to set this on vm
    vm.markup =
      '<html xmlns="https://www.w3.org/1999/xhtml"><![CDATA[' +
      JSON.stringify(markup) +
      ']]></html>';

    return vm.markup;
  }

  // Some DSPs have macros prepended on click urls
  // eg: [[CLICK_URL]]https://.....
  //
  function macroClickUrl(url) {
    if (url) {
      return vm.partner.clickTrackerMacro + encodeURIComponent(url);
    }

    return '';
  }

  function buildVideoLink(creative) {
    var url;

    if (!creative.vastTag && creative.customEngagementUrl) {
      url = creative.customEngagementUrl;

      if (vm.partner.clickTrackerMacro) {
        url = macroClickUrl(url);
      }

      return { url: url };
    }

    // Okay, so apparently there always needs to be SOMETHING in link.url so
    // that it passes the STX validation (it's part of the native spec, although
    // not all our creative types will use this value)
    return {
      url: `https://sfa.sharethrough.com/trackable_redirect/2_2_video_workaround_${creative.id}`
    };
  }

  function buildDisplayLink(creative) {
    var url = creative.mediaUrl;

    if (vm.partner.clickTrackerMacro) {
      url = macroClickUrl(url);
    }

    return { url: url };
  }

  function buildLink(creative) {
    var link;

    if (isVideoCreative(creative)) {
      link = buildVideoLink(creative);
    } else {
      link = buildDisplayLink(creative);
    }

    var clickBeacons = BeaconsService.clickBeacons(creative);

    var trackerUrls = _.map(clickBeacons, function(tracker) {
      if (
        vm.partner.clickTrackerMacro &&
        tracker.url &&
        !isVideoCreative(creative)
      ) {
        return macroClickUrl(tracker.url);
      }

      return tracker.url;
    });

    if (trackerUrls.length > 0) {
      link.clicktrackers = _.compact(trackerUrls);
    }

    return link;
  }

  function buildImpTrackers(creative) {
    var trackers = BeaconsService.impressionBeacons(creative);

    if (isVideoCreative(creative)) {
      trackers.push({ url: `${VIDEO_PIXEL_URL}${RtbPixelService.pixelKey}` });
    } else if (isIsiCreative(creative)) {
      trackers.push({ url: `${DISPLAY_PIXEL_URL}${RtbPixelService.pixelKey}` });
    }

    return _.compact(
      _.map(trackers, function(tracker) {
        return tracker.url;
      })
    );
  }

  function buildBrandLogo(creative) {
    return {
      id: 3,
      required: 1,
      img: { url: creative.brandLogoUrl }
    };
  }

  function buildBrandName(creative) {
    return {
      id: 5,
      required: 1,
      data: { value: creative.advertiser }
    };
  }

  function buildMobileThumbnail(creative) {
    return {
      id: 2,
      required: 1,
      img: { url: creative.thumbnailUrl }
    };
  }

  // We don't actually have desktop thumbnails in here.
  function buildDesktopThumbnail(creative) {
    return {
      id: 4,
      required: 1,
      img: { url: creative.thumbnailUrl }
    };
  }

  function buildHeadline(creative) {
    return {
      id: 1,
      required: 1,
      title: { text: creative.headline }
    };
  }

  function buildDescription(creative) {
    return {
      id: 6,
      required: 1,
      data: {
        value:
          creative.description || "Don't miss this content from our sponsor"
      }
    };
  }

  function buildBanner(creative) {
    return {
      id: 504,
      required: 1,
      data: { value: creative.adm }
    };
  }

  function buildAssets(creative) {
    var assets = [
      buildBrandName(creative),
      buildBrandLogo(creative),
      buildMobileThumbnail(creative),
      buildDesktopThumbnail(creative),
      buildHeadline(creative),
      buildDescription(creative)
    ];

    if (isVideoCreative(creative) && creative.vastTag) {
      assets = assets.concat([buildFakeVast()]);
    }

    if (creative.action === 'banner') {
      assets = [...assets, buildBanner(creative)];
    }

    return assets;
  }

  function buildFakeVast() {
    return { id: 13, required: 1, video: { vasttag: '<FOO></FOO>' } };
  }
}

AdMarkupService.$inject = [
  'BeaconsService',
  'CreativeService',
  'CurrentPartnerService',
  'RtbPixelService'
];
